import React, { useState, useRef, useEffect, useMemo } from "react";
import { useConvertTextToAudioMutation } from "@src/api/graphql-codegen";
import SpeakerIcon from "src/assets/icons/speaker.svg?react";
import styles from "./TextSpeech.module.scss";
import { logger } from '../../utils/logger';

type Props = {
  text: string;
  isAU: boolean;
};

const TextSpeech = (props: Props) => {
  const { text, isAU } = props;
  const [isPlaying, setIsPlaying] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [audioURL, setAudioURL] = useState("");

  const [convertTextToAudio, { loading }] = useConvertTextToAudioMutation({
    onCompleted(data, clientOptions) {
      if (data.convertTextToAudio) {
        setAudioURL(data.convertTextToAudio);
        setLoaded(true);
      }
    },
    onError(error) {
      logger.error(error.message)
      setLoaded(false);
    },
  });

  useEffect(() => {
    convertTextToAudio({
      variables: {
        text,
        isAU,
      },
    });
  }, [text, isAU, convertTextToAudio]);

  const handlePlay = () => {
    if (audioRef.current && audioURL) {
      try {
        audioRef.current.currentTime = 0;
        audioRef.current.src = audioURL;
        audioRef.current.load();
        audioRef.current.play();
        setIsPlaying(true);
      } catch (error) {
        logger.error("Failed to load because no supported source was found");
      }
    }
  };

  const handlePause = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
    }
  };

  const handleAudioEnded = () => {
    setIsPlaying(false);
  };

  const onClickButton = () => {
    if (isPlaying) {
      handlePause();
    } else {
      handlePlay();
    }
  };

  const speakerButtonStyle = useMemo(() => {
    const style = [styles.speakerButton];
    
    if (isPlaying) {
      style.push(styles.speakingButton);
    } else if (loading || !loaded) {
      style.push(styles.loadingSpeakerButton);
    }

    return style.join(' ');
  }, [isPlaying, loaded, loading])

  return (
    <div>
      <audio ref={audioRef} controls={false} onEnded={handleAudioEnded}>
        <source src={audioURL} type="audio/mp3" />
        Your browser does not support the audio element.
      </audio>
      {(
        <SpeakerIcon
          className={speakerButtonStyle}
          onClick={onClickButton}
        />
      )}
    </div>
  );
};

export default TextSpeech;
